import { useMemo, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { generateAgreementPDF } from '../utils/agreementTemplate'
import StoredDocViewer from './StoredDocViewer'
import { practicePdfEntry } from '../utils/practiceAgreement'

/**
 * PracticeAgreementModal — s101 (D496). The physician's OWN agreement, read in the app:
 *   a PDF -> the in-app viewer (StoredDocViewer, page by page; no download, no share sheet);
 *   written sections -> the agreement renderer (practice block included), like Orella's preview.
 * `agreement` is the doctor's active one (share step) or a signed record (`caseId` set: the PDF link
 * is fetched fresh from the case via `getCasePdf`).
 */
export default function PracticeAgreementModal({ isOpen, onClose, agreement, patientName = 'Patient', patientDOB = 'N/A', signatureData = '', signedDate = '', caseId = null, getCasePdf = null, t }) {
  const tr = (k, d, o) => (t ? t(k, { defaultValue: d, ...(o || {}) }) : d)
  const pdf = practicePdfEntry(agreement)
  const [pdfUrl, setPdfUrl] = useState(null)
  useEffect(() => {
    if (!isOpen || !pdf) return undefined
    if (pdf.url) { setPdfUrl(pdf.url); return undefined }
    let alive = true
    if (caseId && typeof getCasePdf === 'function') {
      getCasePdf(caseId).then((r) => { if (alive) setPdfUrl((r && r.url) || null) }).catch(() => { if (alive) setPdfUrl(null) })
    }
    return () => { alive = false }
  }, [isOpen, pdf && pdf.pdfKey, pdf && pdf.url, caseId]) // eslint-disable-line react-hooks/exhaustive-deps

  const html = useMemo(() => {
    if (!isOpen || !agreement || pdf) return ''
    try {
      return generateAgreementPDF({
        patientName, patientDOB, signatureData, signedDate,
        title: agreement.title, sections: agreement.sections, agreementVersion: agreement.version || agreement.agreementVersion,
        effectiveDate: agreement.effectiveDate,
        doctor: { practiceName: agreement.practice || '', firstName: '', lastName: '' },
      })
    } catch (e) { return '' }
  }, [isOpen, agreement, pdf, patientName, patientDOB, signatureData, signedDate])

  if (!isOpen || !agreement) return null
  if (pdf) {
    return (
      <StoredDocViewer open doc={pdfUrl ? { url: pdfUrl, type: 'application/pdf', name: pdf.name } : null}
        caseId={caseId} title={agreement.title || pdf.name} onClose={onClose} t={t} />
    )
  }
  return createPortal(
    <div className="fixed inset-0 z-[200] flex flex-col bg-black/50" onClick={onClose}>
      <div className="flex-1 flex flex-col bg-white rounded-t-[20px] overflow-hidden" onClick={(e) => e.stopPropagation()} data-safe-top="1"
        style={{ marginTop: 'calc(env(safe-area-inset-top, 0px) + 12px)', maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 12px)' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-[15px] font-bold text-[#15354F] truncate">{agreement.title || tr('agreement.practiceFallback', "Your doctor's patient agreement")}</h2>
          <button onClick={onClose} className="p-1.5 -mr-1.5 text-gray-400" aria-label={tr('common.close', 'Close')}><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          <iframe srcDoc={html} title={agreement.title || 'Agreement'} className="w-full border-0" style={{ height: 1400, background: '#fff' }} sandbox="" />
        </div>
      </div>
    </div>,
    document.body)
}
